import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';

interface CmsTheme {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  logoUrl?: string;
  cmsName?: string;
  cmsSlug?: string;
}

interface ThemeContextType {
  theme: CmsTheme;
  isLoading: boolean;
  error: string | null;
  loadThemeFromSlug: (slug: string) => Promise<void>;
  resetToDefault: () => void;
}

const defaultTheme: CmsTheme = {
  colors: {
    primary: '#3B82F6',
    secondary: '#1E40AF',
    accent: '#F59E0B',
    background: '#F8FAFC',
    text: '#1F2937'
  },
  logoUrl: '',
  cmsName: 'CMS Panel',
  cmsSlug: ''
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const location = useLocation();
  const [theme, setTheme] = useState<CmsTheme>(defaultTheme);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Aplicar CSS custom properties quando o tema mudar
  useEffect(() => {
    if (theme.colors) {
      const root = document.documentElement;
      root.style.setProperty('--color-primary', theme.colors.primary);
      root.style.setProperty('--color-secondary', theme.colors.secondary);
      root.style.setProperty('--color-accent', theme.colors.accent);
      root.style.setProperty('--color-background', theme.colors.background);
      root.style.setProperty('--color-text', 'rgb(0, 0, 0)');
    }
  }, [theme.colors]);

  // Extrair slug da URL atual
  const getSlugFromUrl = useCallback(() => {
    const path = location.pathname;
    const segments = path.split('/').filter(segment => segment);
    const possibleSlug = segments[0];
    
    // Se não tiver slug, retornar null
    if (!possibleSlug || possibleSlug === '') {
      return null;
    }
    
    // NÃO rejeitar slugs válidos como 'fourplay', 'u9ion', etc.
    // Apenas rejeitar se for exatamente 'login' sem slug
    if (possibleSlug === 'login') {
      return null;
    }
    
    return possibleSlug;
  }, [location.pathname]);

  // Carregar tema baseado no slug da URL
  const loadThemeFromSlug = useCallback(async (slug: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Buscar dados públicos do CMS usando o slug da URL
      const publicData = await apiService.getCmsPublicData(slug);
      
      if (publicData?.success && publicData?.cms) {
        const cms = publicData.cms;
        setTheme({
          colors: cms.colors || defaultTheme.colors,
          logoUrl: cms.logoUrl || '',
          cmsName: cms.name || 'CMS Panel',
          cmsSlug: slug
        });
      } else {
        // Se não encontrar dados públicos, usar tema padrão com slug
        setTheme({
          ...defaultTheme,
          cmsName: slug || 'CMS Panel',
          cmsSlug: slug
        });
      }
    } catch (err) {
      console.warn('Não foi possível carregar tema público do CMS, usando tema padrão:', err);
      setTheme({
        ...defaultTheme,
        cmsName: slug || 'CMS Panel',
        cmsSlug: slug
      });
      setError(null); // Não mostrar erro que quebre a UI
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Resetar para tema padrão
  const resetToDefault = () => {
    setTheme(defaultTheme);
    setError(null);
  };

  // Carregar tema baseado na URL quando mudar
  useEffect(() => {
    const slug = getSlugFromUrl();
    if (slug) {
      loadThemeFromSlug(slug);
    } else {
      // Usar tema padrão se não há slug válido
      setTheme(defaultTheme);
    }
  }, [getSlugFromUrl, loadThemeFromSlug]);

  const value: ThemeContextType = {
    theme,
    isLoading,
    error,
    loadThemeFromSlug,
    resetToDefault
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
